import { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useDropzone } from 'react-dropzone';
import { styled } from '@mui/material/styles';
import ReactQuill from 'react-quill-new';
import 'react-quill-new/dist/quill.snow.css';
import SkillsInput from '../common/SkillsInput';

import {
  Box,
  TextField,
  Button,
  Typography,
  IconButton,
  Snackbar,
  Alert as MuiAlert,
  Tab,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio
} from '@mui/material';
import {
  Upload,
  Close,
  PictureAsPdf as PictureAsPdfIcon,
  Description as DescriptionIcon,
  Slideshow as SlideshowIcon,
  Videocam as VideocamIcon,
  Audiotrack as AudiotrackIcon,
  InsertDriveFile as InsertDriveFileIcon,
  TableChart as TableChartIcon,
  School as SchoolIcon,
  FolderZip as FolderZipIcon
} from '@mui/icons-material';
import { contentService } from '../../services/api';

const FormSection = styled(Box)(({ theme }) => ({
  marginBottom: theme.spacing(4)
}));

const DropzoneArea = styled(Box)(({ theme }) => ({
  border: `2px dashed ${theme.palette.grey[300]}`,
  borderRadius: '12px',
  padding: theme.spacing(4),
  backgroundColor: theme.palette.grey[50],
  textAlign: 'center',
  cursor: 'pointer',
  transition: 'all 0.3s ease',
  '&:hover': {
    borderColor: theme.palette.primary.main,
    backgroundColor: theme.palette.grey[100]
  }
}));

const PreviewImage = styled('img')({
  maxWidth: '100%',
  maxHeight: '200px',
  objectFit: 'cover',
  borderRadius: '8px'
});

const modules = {
  toolbar: {
    container: [
      [{ 'header': '1' }, { 'header': '2' }, { 'font': [] }],
      [{ size: [] }],
      ['bold', 'italic', 'underline', 'strike', 'blockquote'],
      [{ 'list': 'ordered' }, { 'list': 'bullet' }, { 'indent': '-1' }, { 'indent': '+1' }],
      ['clean']
    ]
  },
  clipboard: { matchVisual: false }
};

const formats = [
  'header', 'font', 'size', 'bold', 'italic', 'underline', 'strike', 'blockquote',
  'list', 'bullet', 'indent', 'link', 'image', 'video'
];

const titleFromFileName = (fileName) => {
  const lastDot = fileName.lastIndexOf('.');
  return lastDot > 0 ? fileName.slice(0, lastDot) : fileName;
};

/**
 * ContentForm is a React component for creating and editing content.
 * It displays a form with fields for entering content name, description, duration, and content source.
 * It also allows for tagging the content.
 * @returns {React.ReactElement} The rendered form.
 */
const ContentForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const prioritizeOnCreate = !id && location.state?.is_prioritized === true;
  const [loading, setLoading] = useState(false);
  const [saveStatus, setSaveStatus] = useState('publish');
  const [formData, setFormData] = useState({
    content_id: '',
    name: typeof location.state?.prefill === 'string' ? location.state.prefill : '',
    description: '',
    duration: '',
    // tags: [],
    skills: [],
    content_url: '',
    file: null,
    // accessibility / audience type: 1=Internal,2=External,3=Both
    audience_type: '3'
  });
  const [preview, setPreview] = useState('');
  const [originalFileName, setOriginalFileName] = useState(''); // Track original filename in edit mode
  const [hasNewFile, setHasNewFile] = useState(false); // Track if new file selected in edit mode
  const [errors, setErrors] = useState({});
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });
  // File dropzone for file upload tab
  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop: (acceptedFiles) => {
      const file = acceptedFiles[0];
      if (file) {
        setFormData(prev => ({
          ...prev,
          file,
          name: titleFromFileName(file.name)
        }));
        setPreview(URL.createObjectURL(file));
        if (id) setHasNewFile(true);
      }
    },
    accept: {
      'application/pdf': ['.pdf'],
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
      'text/plain': ['.txt'],
      'application/zip': ['.zip'],
      'application/x-zip-compressed': ['.zip'],
      'application/scorm+zip': ['.zip'],
      'video/mp4': ['.mp4'],
      'video/webm': ['.webm'],
      // Removed 9 Oct 2026 (K21.1 pt 17): .doc (old Word files cannot be read; save as .docx) and the .mpeg and
      // .mpga audio names (not played or transcribed here).
      'audio/mpeg': ['.mp3'],
      'audio/mp4': ['.m4a'],
      'audio/wav': ['.wav']
    },
    maxFiles: 1,
  });

  useEffect(() => {
    if (id) {
      loadContentData();
    }
  }, [id]);

  const loadContentData = async () => {
    setLoading(true);
    try {
      const response = await contentService.getContentById(id);
      const data = response.data.content;

      const isUrl = /^https?:\/\//i.test(data.content_url || '') || String(data.content_url || '').startsWith('www.');

      setFormData({
        content_id: data.id,
        name: data.name,
        description: data.description || '',
        duration: data.duration || '',
        // tags: Array.isArray(data.tags) ? data.tags : (data.tags ? data.tags.split(',') : []),
        skills: (data.skills || []).map((skill) => skill && typeof skill === 'object' ? skill.id : skill),
        content_url: isUrl ? '' : (data.content_url || ''),
        file: null,
        audience_type: data.audience_type ? String(data.audience_type) : '3'
      });
      setSaveStatus(data.status === 1 ? 'draft' : 'publish');
      setOriginalFileName(isUrl ? '' : (data.content_url || ''));

      if (!isUrl && data.content_url) {
        setPreview(`/uploads/contents/${data.content_url}`);
      } else if (isUrl) {
        setPreview('');
        setSnackbar({
          open: true,
          message: 'URL content is no longer supported. Upload a file to replace this item.',
          severity: 'warning'
        });
      }
    } catch (error) {
      console.error('Error loading content data:', error);
      setSnackbar({
        open: true,
        message: 'Error loading content data',
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleCancel = () => {
    navigate('/contents');
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Content name is required';
    }

    // accessibility/audience must be selected
    if (!formData.audience_type) {
      newErrors.audience_type = 'Accessibility is required';
    }

    // if (!formData.tags || formData.tags.length === 0) {
    //   newErrors.tags = 'At least one tag is required';
    // }

    if (!formData.file && !preview) {
      newErrors.file = 'File is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const isValid = validateForm();
    if (!isValid) {
      setSnackbar({
        open: true,
        message: 'Please fill all required fields',
        severity: 'error'
      });
      return;
    }

    const submitter = e.nativeEvent?.submitter;
    const currentSaveStatus = submitter?.value || saveStatus || 'publish';

    setLoading(true);
    try {
      const submitData = new FormData();
      submitData.append('content_id', formData.content_id || 0);
      submitData.append('name', formData.name);
      submitData.append('description', formData.description);
      submitData.append('duration', formData.duration);
      // submitData.append('tags', formData.tags.join(','));
      submitData.append('skills', formData.skills.join(','));
      // include accessibility / audience_type
      submitData.append('audience_type', formData.audience_type);
      submitData.append('action', 'file');
      submitData.append('save_status', currentSaveStatus);
      if (prioritizeOnCreate) submitData.append('is_prioritized', 'true');

      if (formData.file || preview) {
        // In edit mode: if formData.file exists, a NEW file was selected (re-upload)
        // If only preview exists (no new file), keep existing file
        submitData.append('file_names', formData.name);
        if (formData.file) {
          // New file selected - trigger re-ingestion on backend
          submitData.append('content_file', formData.file);
          submitData.append('replace_file', 'true');
        } else if (preview && originalFileName) {
          // Edit mode with existing file, no replacement
          submitData.append('keep_existing_file', 'true');
          submitData.append('existing_filename', originalFileName);
        }
      }
      
      const response = await contentService.saveContent(submitData);
      if (response.data.success) {
        setSnackbar({
          open: true,
          message: 'Content saved successfully!',
          severity: 'success'
        });

        setTimeout(() => {
          navigate('/contents');
        }, 1000);
      } else {
        throw new Error(response.data.message || 'Failed to save content');
      }
    } catch (error) {
      console.error('Error saving content:', error.response.data.message);
      setSnackbar({
        open: true,
        message: error.response.data.message || 'Error saving content',
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  const getFileType = (filename) => {
    if (!filename) return 'unknown';

    const extension = filename.toLowerCase().split('.').pop();

    if (['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp', 'svg'].includes(extension)) {
      return 'image';
    } else if (['pdf'].includes(extension)) {
      return 'pdf';
    } else if (['doc', 'docx', 'rtf', 'txt'].includes(extension)) {
      return 'document';
    } else if (['ppt', 'pptx'].includes(extension)) {
      return 'presentation';
    } else if (['xls', 'xlsx', 'csv'].includes(extension)) {
      return 'spreadsheet';
    } else if (['zip'].includes(extension)) {
      // Check if it might be a SCORM package based on filename
      if (filename.toLowerCase().includes('scorm')) {
        return 'scorm';
      }
      return 'archive';
    } else if (['mp4', 'avi', 'mov', 'wmv', 'flv', 'webm'].includes(extension)) {
      return 'video';
    } else if (['mp3', 'wav', 'ogg', 'm4a', 'flac'].includes(extension)) {
      return 'audio';
    }
    return 'file';
  };
  return (
    <Box sx={{ maxWidth: 1200, mx: 'auto', p: 3 }}>
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        sx={{ mt: 7 }}
      >
        <MuiAlert
          severity={snackbar.severity}
          variant="filled"
          onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
        >
          {snackbar.message}
        </MuiAlert>
      </Snackbar>

      <Typography variant="h4" sx={{ mb: 1 }}>
        {id ? 'Edit Content' : 'Create New Content'}
      </Typography>
      <Typography variant="body2" sx={{ mb: 3, color: 'text.secondary' }}>
        Choose whether to save this item as a draft or publish it immediately.
      </Typography>
      <form onSubmit={handleSubmit}>
        <FormSection>
          <Typography variant="h6" sx={{ mb: 3 }}>
            Basic Information
          </Typography>
          <TextField
            fullWidth
            label="Content Name"
            name="name"
            value={formData.name}
            onChange={handleChange}
            variant="outlined"
            sx={{ mb: 3 }}
            error={!!errors.name}
            helperText={errors.name}
            required
          />
        </FormSection>
        <FormSection>
          <FormControl component="fieldset" error={!!errors.audience_type} required sx={{ mb: 3 }}>
            <FormLabel component="legend">Accessibility</FormLabel>
            <RadioGroup row name="audience_type" value={formData.audience_type} onChange={handleChange}>
              <FormControlLabel value="1" control={<Radio />} label="Internal" />
              <FormControlLabel value="2" control={<Radio />} label="External" />
              <FormControlLabel value="3" control={<Radio />} label="Both" />
            </RadioGroup>
            {errors.audience_type && (
              <Typography color="error" variant="caption">
                {errors.audience_type}
              </Typography>
            )}
          </FormControl>
        </FormSection>
        <FormSection>
          {id ? (
            <TextField
              fullWidth
              label="Duration (minutes)"
              name="duration"
              value={formData.duration}
              onChange={handleChange}
              variant="outlined"
              sx={{ mb: 3 }}
              type="number"
              inputProps={{ min: 0, step: 1 }}
              error={!!errors.duration}
              helperText={errors.duration || 'Calculated from the file. You can adjust it here.'}
            />
          ) : (
            <Typography variant="body2" sx={{ mb: 3, color: 'text.secondary' }}>
              Duration will be calculated automatically after the file is processed.
            </Typography>
          )}
        </FormSection>

        <FormSection>
          <Typography variant="h6" sx={{ mb: 3 }}>
            Content Description
          </Typography>
          <ReactQuill
            theme="snow"
            value={formData.description}
            onChange={(content) => setFormData(prev => ({
              ...prev,
              description: content
            }))}
            style={{ height: '200px', marginBottom: '50px' }}
            modules={modules}
            formats={formats}
          />
        </FormSection>

        <FormSection>
          <Typography variant="h6" sx={{ mb: 3 }}>
            Content Source
          </Typography>

          <DropzoneArea {...getRootProps()}>
              <input {...getInputProps()} />
              {preview ? (
                <Box position="relative">
                  <IconButton
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      setPreview('');
                      setFormData(prev => ({ ...prev, file: null }));
                      if (id) setHasNewFile(false);
                    }}
                    sx={{
                      position: 'absolute',
                      top: 8,
                      right: 8,
                      backgroundColor: 'white',
                      '&:hover': { backgroundColor: 'white' }
                    }}
                  >
                    <Close />
                  </IconButton>

                  {/* Determine what to display based on file type */}
                  {getFileType(formData.file?.name || originalFileName || formData.content_url) === 'image' ? (
                    <PreviewImage src={preview} alt="Content preview" />
                  ) : (
                    <Box
                      sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        p: 3,
                        backgroundColor: 'grey.100',
                        borderRadius: 1
                      }}
                    >
                      {/* Show appropriate icon based on file type */}
                      {getFileType(formData.file?.name || originalFileName || formData.content_url) === 'pdf' && (
                        <PictureAsPdfIcon sx={{ fontSize: 48, color: 'error.main', mb: 2 }} />
                      )}
                      {getFileType(formData.file?.name || originalFileName || formData.content_url) === 'document' && (
                        <DescriptionIcon sx={{ fontSize: 48, color: 'primary.main', mb: 2 }} />
                      )}
                      {getFileType(formData.file?.name || originalFileName || formData.content_url) === 'presentation' && (
                        <SlideshowIcon sx={{ fontSize: 48, color: 'warning.main', mb: 2 }} />
                      )}
                      {getFileType(formData.file?.name || originalFileName || formData.content_url) === 'spreadsheet' && (
                        <TableChartIcon sx={{ fontSize: 48, color: 'success.main', mb: 2 }} />
                      )}
                      {getFileType(formData.file?.name || originalFileName || formData.content_url) === 'scorm' && (
                        <SchoolIcon sx={{ fontSize: 48, color: 'info.main', mb: 2 }} />
                      )}
                      {getFileType(formData.file?.name || originalFileName || formData.content_url) === 'archive' && (
                        <FolderZipIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
                      )}
                      {getFileType(formData.file?.name || originalFileName || formData.content_url) === 'video' && (
                        <VideocamIcon sx={{ fontSize: 48, color: 'success.main', mb: 2 }} />
                      )}
                      {getFileType(formData.file?.name || originalFileName || formData.content_url) === 'audio' && (
                        <AudiotrackIcon sx={{ fontSize: 48, color: 'info.main', mb: 2 }} />
                      )}
                      {getFileType(formData.file?.name || originalFileName || formData.content_url) === 'file' && (
                        <InsertDriveFileIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
                      )}

                      {/* Display the filename */}
                      <Typography variant="body1" sx={{ wordBreak: 'break-word', textAlign: 'center' }}>
                        {formData.file ? formData.file.name : originalFileName || formData.content_url}
                      </Typography>

                      {/* Display file type label */}
                      <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>
                        {getFileType(formData.file?.name || originalFileName || formData.content_url) === 'scorm' ? 'SCORM Package' :
                          getFileType(formData.file?.name || originalFileName || formData.content_url) === 'document' ? 'Document' :
                            getFileType(formData.file?.name || originalFileName || formData.content_url) === 'video' ? 'Video' :
                              getFileType(formData.file?.name || originalFileName || formData.content_url) === 'audio' ? 'Audio' :
                                getFileType(formData.file?.name || originalFileName || formData.content_url) === 'pdf' ? 'PDF Document' : 'File'}
                      </Typography>
                    </Box>
                  )}
                </Box>
              ) : (
                <Box>
                  <Upload sx={{ fontSize: 48, color: 'primary.main', mb: 2 }} />
                  <Typography variant="body1" sx={{ mb: 1 }}>
                    {id && preview ? 'Replace file or drag and drop here' : 'Drag and drop your file here'}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    or click to browse
                  </Typography>
                  {id && preview && (
                    <Typography variant="caption" color="textSecondary" sx={{ mt: 1, display: 'block' }}>
                      Current: {originalFileName}
                    </Typography>
                  )}
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 2, display: 'block' }}>
                    {/* DOCX, as Add content says (K21.2 pt 20.1.b) */}
                    Supported formats: PDF, DOCX, text, MP4, WebM, MP3, M4A, WAV, SCORM (.zip). Up to 50 MB.
                  </Typography>
                  {errors.file && (
                    <Typography color="error" variant="caption" display="block" sx={{ mt: 1 }}>
                      {errors.file}
                    </Typography>
                  )}
                </Box>
              )}
          </DropzoneArea>
        </FormSection>

        {id && (
          <FormSection>
            <SkillsInput
              value={formData.skills}
              onChange={(newSkills) => setFormData(prev => ({
                ...prev,
                skills: newSkills
              }))}
              error={!!errors.skills}
              helperText={errors.skills}
              required={false}
            />
          </FormSection>
        )}

        <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
          <Button
            type="button"
            variant="outlined"
            className="add-course-btn"
            disabled={loading}
            onClick={handleCancel}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            value="draft"
            variant="outlined"
            className="add-course-btn"
            disabled={loading}
            onClick={() => setSaveStatus('draft')}
          >
            Save as Draft
          </Button>
          <Button
            type="submit"
            value="publish"
            variant="contained"
            className="add-course-btn"
            disabled={loading}
            onClick={() => setSaveStatus('publish')}
          >
            {loading ? 'Saving...' : 'Publish'}
          </Button>
        </Box>
      </form>
    </Box>
  );
};

export default ContentForm;